import { SvgIcon } from '../../../Icon';
import React, { useState } from 'react';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import styled from 'styled-components';
import { DashboardSlackDestination } from './DashboardSlackDestination';
import { useQuery } from '@apollo/client';
import {
  IGetSlackConnectionQuery,
  IGetSlackConnectionQueryVariables,
  ISlackDestinationFieldsFragment,
  SlackEntityType,
  SlackSourceType,
} from '../../../../utils/apollo/graphqlSchema';
import { GET_SLACK_CONNECTION } from '../../../../utils/apollo/slack/queries';
import { MaybeNil } from '../../../../utils/interfaces';
import useSlackConnect from '../../../Slack/useSlackConnect';
import isNil from 'lodash/isNil';
import useSlackDisconnect from '../../../Slack/useSlackDisconnect';
import { floatingComponentStyles } from '../../../../styles/shareableStyles';
import { FloatingLayer } from '../../../FloatingManager';

interface IProps {
  dashboardBaseId: string;
  disabled: boolean;
}

const Panel = styled.div`
  padding: 8px 8px 8px 16px;
  min-width: 240px;
  max-width: 340px;
  ${floatingComponentStyles};
`;
const Wrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: 8px;
  min-height: 28px;
`;

const DashboardNotificationSettings = ({
  dashboardBaseId,
  disabled,
}: IProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset({ mainAxis: 5, crossAxis: 5 }),
      flip(),
      shift({ padding: 5 }),
    ],
    placement: 'bottom-end',
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context, { enabled: !disabled });
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const [slackDestination, setSlackDestination] =
    useState<MaybeNil<ISlackDestinationFieldsFragment>>(null);
  const [destinationUpdating, setDestinationUpdating] = useState(false);

  const { loading: connectionLoading } = useQuery<
    IGetSlackConnectionQuery,
    IGetSlackConnectionQueryVariables
  >(GET_SLACK_CONNECTION, {
    variables: {
      input: {
        type: SlackSourceType.Entity,
        entitySource: {
          entityType: SlackEntityType.Dashboard,
          entityId: dashboardBaseId,
        },
      },
    },
    onCompleted: ({ slackConnection }) =>
      setSlackDestination(slackConnection?.destination),
  });

  const { connectDashboard } = useSlackConnect();
  const { disconnectDashboard } = useSlackDisconnect();

  const updateDestination = async (
    newDestination: MaybeNil<ISlackDestinationFieldsFragment>,
  ) => {
    try {
      setDestinationUpdating(true);
      if (!isNil(newDestination)) {
        const connection = await connectDashboard(
          dashboardBaseId,
          newDestination,
        );
        setSlackDestination(connection);
      } else {
        await disconnectDashboard(dashboardBaseId);
        setSlackDestination(null);
      }
    } finally {
      setDestinationUpdating(false);
    }
  };

  const loading = connectionLoading || destinationUpdating;

  return (
    <>
      <SvgIcon
        ref={refs.setReference}
        {...getReferenceProps()}
        name="gear"
        width={24}
        height={24}
        isDisabled={disabled}
        isActive={isOpen}
        isHoverable={true}
      />
      {isOpen && (
        <FloatingLayer>
          <Panel
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <Wrapper>
              <SvgIcon name="slack-logo" width={16} height={16} />
              <DashboardSlackDestination
                loading={loading}
                value={slackDestination}
                onChange={updateDestination}
              />
            </Wrapper>
          </Panel>
        </FloatingLayer>
      )}
    </>
  );
};

export default DashboardNotificationSettings;
